import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API, useAuth, getDashboardRoute } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { User, Lock, ChevronDown, X, AlertCircle, ChevronRight, ChevronLeft, Check } from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";

const INVESTMENT_FOCUS_OPTIONS = [
  "Single Family", "Multi Family", "Commercial", "Industrial",
  "Flips", "Development", "Condos/Townhomes", "Land",
  "Luxury Properties", "Home Additions", "Rentals",
];

const CAPITAL_OPTIONS = [
  "Under $100,000", "$100,000 - $250,000", "$250,000 - $500,000",
  "$500,000 - $1,000,000", "$1,000,000 - $5,000,000", "$5,000,000+",
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming",
];

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
    name: "", phone: "", address: "", state: "", notes: "",
    available_capital: "", primary_investment_focus: [],
    areas_of_focus_state: "", years_experience: "",
  });

  const [pwd, setPwd] = useState({
    new_password: "", confirm_password: "",
  });

  const role = user?.role;
  const showInvestmentSection = role === "investor" || role === "wholesaler";
  const isIncomplete =
    role === "investor" &&
    (!user?.available_capital ||
      !user?.primary_investment_focus?.length ||
      !user?.areas_of_focus_state ||
      (user?.years_experience == null && user?.years_experience !== 0));

  const exitProfile = async () => {
    await checkAuth();
    navigate(getDashboardRoute(role), { replace: true });
  };
  const steps = [
    { id: "personal", label: "Personal Info", icon: User },
    ...(showInvestmentSection ? [{ id: "investment", label: "Investment Details", icon: ChevronRight }] : []),
    { id: "security", label: "Security", icon: Lock },
  ];

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/profile`, {
        withCredentials: true, headers: getAuthHeaders(),
      });
      setForm({
        name: data.name || "",
        phone: data.phone || "",
        address: data.address || "",
        state: data.state || "",
        notes: data.notes || "",
        available_capital: data.available_capital || "",
        primary_investment_focus: Array.isArray(data.primary_investment_focus)
          ? data.primary_investment_focus
          : data.primary_investment_focus ? [data.primary_investment_focus] : [],
        areas_of_focus_state: data.areas_of_focus_state || "",
        years_experience: data.years_experience ?? "",
      });
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        phone: form.phone || null,
        address: form.address || null,
        state: form.state || null,
        notes: form.notes || null,
      };
      if (showInvestmentSection) {
        payload.available_capital = form.available_capital || null;
        payload.primary_investment_focus = form.primary_investment_focus.length > 0 ? form.primary_investment_focus : null;
        payload.areas_of_focus_state = form.areas_of_focus_state || null;
        payload.years_experience = form.years_experience === "" ? null : Number(form.years_experience);
      }
      await axios.put(`${API}/auth/profile`, payload, {
        withCredentials: true, headers: getAuthHeaders(),
      });
      toast.success("Profile updated successfully");
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwd.new_password) return;
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setChangingPwd(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { new_password: pwd.new_password },
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success("Password updated successfully");
      setPwd({ new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPwd(false);
    }
  };

  const toggleFocus = (opt) => {
    setForm((prev) => ({
      ...prev,
      primary_investment_focus: prev.primary_investment_focus.includes(opt)
        ? prev.primary_investment_focus.filter((f) => f !== opt)
        : [...prev.primary_investment_focus, opt],
    }));
  };

  const isLastStep = step === steps.length - 1;
  const currentStepId = steps[step]?.id;

  const handleNext = async () => {
    if (currentStepId === "personal" || currentStepId === "investment") {
      await handleSave();
    }
    if (!isLastStep) setStep((s) => s + 1);
  };

  const handleBack = () => setStep((s) => s - 1);

  if (loading) {
    return (
      <DashboardLayout title="Profile">
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner border-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Profile Settings">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">

        {/* Incomplete profile banner */}
        {isIncomplete && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <AlertCircle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Complete your investor profile</p>
              <p className="text-sm text-muted-foreground mt-0.5">Please fill in your investment details before accessing the platform.</p>
            </div>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {steps.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <button
                onClick={() => setStep(i)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  i === step
                    ? "bg-blue-600 text-white"
                    : i < step
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {i < step ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <span className="w-4 h-4 flex items-center justify-center text-xs">{i + 1}</span>
                )}
                {s.label}
              </button>
              {i < steps.length - 1 && <div className="w-6 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Account info — always visible at top */}
        <div className="flex items-center gap-4 px-1">
          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-sm">{user?.email}</p>
            <p className="text-xs text-muted-foreground capitalize">{role}</p>
          </div>
        </div>

        {/* Step: Personal Info */}
        {currentStepId === "personal" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="555-555-5555"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <AddressAutocomplete
                  id="address"
                  label="Address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  onPlaceSelected={(addressData) => {
                    setForm((prev) => ({
                      ...prev,
                      address: addressData.formatted_address || prev.address,
                      state: addressData.state || prev.state,
                    }));
                  }}
                  placeholder="Enter your address"
                  data-testid="profile-address-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={form.state || "none"}
                    onValueChange={(v) => setForm({ ...form, state: v === "none" ? "" : v })}
                  >
                    <SelectTrigger id="state" data-testid="profile-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Select state —</SelectItem>
                      {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything else we should know?"
                  rows={3}
                  data-testid="profile-notes-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Investment Details */}
        {currentStepId === "investment" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Investment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="liquid">Available Liquid Capital</Label>
                  <Select
                    value={form.available_capital || "none"}
                    onValueChange={(v) => setForm({ ...form, available_capital: v === "none" ? "" : v })}
                  >
                    <SelectTrigger id="liquid" data-testid="profile-capital-select">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Select range —</SelectItem>
                      {CAPITAL_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Primary Investment Focus</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between font-normal h-auto min-h-[40px]"
                        data-testid="profile-focus-trigger"
                      >
                        {form.primary_investment_focus.length > 0 ? (
                          <span className="flex flex-wrap gap-1">
                            {form.primary_investment_focus.map((item) => (
                              <Badge key={item} variant="secondary" className="text-xs px-2 py-0.5 gap-1">
                                {item}
                                <X className="w-3 h-3 cursor-pointer hover:text-destructive"
                                  onClick={(e) => { e.stopPropagation(); toggleFocus(item); }} />
                              </Badge>
                            ))}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Select investment focus</span>
                        )}
                        <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-2" align="start">
                      <div className="space-y-1 max-h-[240px] overflow-y-auto">
                        {INVESTMENT_FOCUS_OPTIONS.map((opt) => {
                          const isSelected = form.primary_investment_focus.includes(opt);
                          return (
                            <div key={opt}
                              className="flex items-center space-x-2 rounded-md px-2 py-1.5 hover:bg-muted cursor-pointer"
                              onClick={() => toggleFocus(opt)}
                            >
                              <Checkbox checked={isSelected} onCheckedChange={() => {}} />
                              <span className="text-sm">{opt}</span>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="focus-state">Areas of Focus — State</Label>
                  <Select
                    value={form.areas_of_focus_state || "none"}
                    onValueChange={(v) => setForm({ ...form, areas_of_focus_state: v === "none" ? "" : v })}
                  >
                    <SelectTrigger id="focus-state" data-testid="profile-focus-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Select state —</SelectItem>
                      {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="years">Years of Experience</Label>
                  <Input
                    id="years"
                    type="number"
                    min="0"
                    value={form.years_experience}
                    onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                    data-testid="profile-years-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Security */}
        {currentStepId === "security" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-pwd">New Password</Label>
                  <Input
                    id="new-pwd"
                    type="password"
                    value={pwd.new_password}
                    onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pwd">Confirm New Password</Label>
                  <Input
                    id="confirm-pwd"
                    type="password"
                    value={pwd.confirm_password}
                    onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })}
                    minLength={8}
                    className={pwd.confirm_password && pwd.new_password !== pwd.confirm_password ? "border-red-500" : ""}
                    data-testid="confirm-password-input"
                  />
                  {pwd.confirm_password && pwd.new_password !== pwd.confirm_password && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Optional — leave blank to keep your current password. Must be at least 8 characters.</p>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={step === 0 ? exitProfile : handleBack}
            data-testid="profile-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step === 0 ? "Cancel" : "Back"}
          </Button>

          {currentStepId === "security" ? (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={handleChangePassword}
                disabled={changingPwd || !pwd.new_password || pwd.new_password !== pwd.confirm_password}
                variant="outline"
                data-testid="change-password-btn"
              >
                {changingPwd ? "Updating..." : "Update Password"}
              </Button>
              <Button
                type="button"
                onClick={exitProfile}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="profile-done-btn"
              >
                Done
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="profile-next-btn"
            >
              {saving ? "Saving..." : isLastStep ? "Save" : "Save & Continue"}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
